import { Collection, Document } from 'firestorter';
import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';
import CoachHistory from './CoachHistory';

const FormState = {
  NEW: 'NEW',
  EDIT: 'EDIT',
};

const FormType = {
  LEAD: 'LEAD',
  POST_PAYMENT: 'POST_PAYMENT',
};

class Form extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.FORMS}/${id}`, opts);
  }

  get formName() {
    return this.data.formName;
  }

  get coach() {
    return this.data.coach;
  }

  get coachId() {
    return this.data.coachId;
  }

  get hiddenFields() {
    return this.data.hiddenFields;
  }

  get fields() {
    return this.data.fields;
  }

  get endScreenContent() {
    return this.data.endScreenContent;
  }

  get isArchived() {
    return !!this.data.isArchived;
  }

  get type() {
    return this.data.type;
  }

  get shouldShowTextBlastConsent() {
    return !!this.data.shouldShowTextBlastConsent;
  }

  /**
   * The single global template doc cloned at coach provisioning. Never editable or
   * archivable from the UI. Historical name — it does NOT mean "this coach's default":
   * a coach's default PPF is the `coach.defaultPostPaymentFormId` pointer.
   * @returns {boolean} - Whether the form is the master template.
   */
  get isDefault() {
    return !!this.data.isDefault;
  }

  /**
   * Gets the forms.
   *
   * @param {boolean} fetchArchivedForms - Whether or not to fetch archived forms
   * @returns {Promise<Collection>} The collection of forms from firestore
   */
  static async getForms(fetchArchivedForms = false) {
    const formsCollection = new Collection(Collections.FORMS, {
      createDocument: ({ id }, opts) => new Form(id, opts),
      query: (ref) => {
        let query = ref.orderBy('coach', 'asc');
        if (!fetchArchivedForms) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
    });
    await formsCollection.fetch();
    return formsCollection;
  }

  static async addForm(data) {
    const formsCollection = new Collection(Collections.FORMS);
    const { id } = await formsCollection.add(data);
    return id;
  }

  /**
   * Reserves a fresh, unwritten form doc id — no network call, no data written. Lets a caller
   * upload the form's storage JSON to this id's path BEFORE the Firestore doc exists (see
   * addFormWithId), so a doc-create failure only ever leaves an orphaned, unreferenced storage
   * file behind instead of a doc that a fast server-side trigger (e.g. onFormCreated promoting
   * a coach's first PPF to their default) could already have acted on before a rollback lands.
   *
   * @returns {string} - An unused form doc id.
   */
  static reserveFormId() {
    return BaseDocument.reserveId(Collections.FORMS);
  }

  /**
   * Creates a form doc at a previously reserved id (see reserveFormId), instead of letting
   * Firestore assign one (as addForm does). Mirrors firestorter's own documented custom-id
   * pattern: a Document constructed with an explicit path, then `.set(data)`.
   *
   * @param {string} id - A doc id from reserveFormId().
   * @param {object} data - The form fields to write.
   * @returns {Promise<void>}
   */
  static async addFormWithId(id, data) {
    const formDoc = new Document(`${Collections.FORMS}/${id}`);
    await formDoc.set(data);
  }

  /**
   * Archives a form.
   * @return {Promise<void>}
   */
  async archive() {
    return this.updateFields({
      isArchived: true,
    });
  }

  /**
   * Unarchives a form.
   * @return {Promise<void>}
   */
  async unarchive() {
    return this.updateFields({
      isArchived: false,
    });
  }

  /**
   * Retrieves all active forms of a specific type associated with a given coach ID.
   * @param {string} coachId - The ID of the coach whose forms are to be retrieved.
   * @param {string} formType - The type of form to retrieve (e.g., FormType.POST_PAYMENT, FormType.LEAD).
   * @returns {Promise<Array<Form>>} - A promise that resolves to an array of documents representing the forms.
   */
  static async getFormsByCoachId(coachId, formType) {
    const formsCollection = this.getCoachFormsCollection(coachId, formType);
    await formsCollection.fetch();
    return formsCollection.docs;
  }

  /**
   * Live collection of a coach's active (non-archived) forms of the given type.
   * Dereference it from an observer component to keep it real-time: firestorter
   * attaches the snapshot listener while the collection is observed, so the list
   * follows form additions and edits without manual refetches.
   *
   * @param {string} coachId - The ID of the coach whose forms are to be observed.
   * @param {string} formType - The form type (see FormType) to observe.
   * @returns {Collection} - The firestorter collection of the coach's forms.
   */
  static getCoachFormsCollection(coachId, formType) {
    return new Collection(Collections.FORMS, {
      createDocument: ({ id }, opts) => new Form(id, opts),
      query: (ref) => ref
        .where('coachId', '==', coachId)
        .where('isArchived', '==', false)
        .where('type', '==', formType),
    });
  }

  /**
   * The global master PPF template: the single `isDefault` form doc cloned at coach
   * provisioning (mirrors the server's ppfCreation query). Coach-created PPFs must
   * always start from it so the mandatory system questions are present.
   *
   * @returns {Promise<Form|null>} - The master template form, or null when missing.
   */
  static async getMasterPostPaymentTemplate() {
    const templateCollection = new Collection(Collections.FORMS, {
      createDocument: ({ id }, opts) => new Form(id, opts),
      query: (ref) => ref
        .where('isDefault', '==', true)
        .where('type', '==', FormType.POST_PAYMENT)
        .limit(1),
    });
    await templateCollection.fetch();
    return templateCollection.docs[0] || null;
  }

  /**
   * Retrieves the coach's active post payment forms that a product may pin.
   * Excludes the master template: it is the global doc cloned at coach
   * provisioning, never a form a coach's clients should be served — and it
   * carries the coach id of whichever account happens to hold it.
   *
   * @param {string} coachId - The ID of the coach whose forms are to be retrieved.
   * @returns {Promise<Array<Form>>} - The coach's pinnable post payment forms.
   */
  static async getPinnablePostPaymentForms(coachId) {
    const forms = await this.getFormsByCoachId(coachId, FormType.POST_PAYMENT);
    return forms.filter((form) => !form.isDefault);
  }

  /**
 * Archives all lead forms associated with a specific coach and logs the action in history.
 *
 * @param {string} coachId - The ID of the coach whose lead forms are to be archived.
 * @param {string} authUserId - The ID of the user performing the archive action.
 * @param {string} authUserName - The name of the user performing the archive action.
 * @param {string} historyType - The type of history event to be logged (e.g., "ARCHIVE").
 * @throws {Error} If no lead forms are available (either none created or all archived).
 * @returns {Promise<void>} A promise that resolves once all lead forms are archived and history records are created.
 */
  static async archiveLeadForms(coachId, authUserId, authUserName, historyType) {
    const leadForms = await this.getFormsByCoachId(coachId, FormType.LEAD);
    if (!leadForms || leadForms.length === 0) {
      throw new Error('No lead forms available - none created or all archived.');
    }
    await Promise.all(
      leadForms.map(async (form) => {
        await form.archive();
        await CoachHistory.addCoachHistoryItem({
          type: historyType,
          coachId: form.data.coachId,
          actionedBy: authUserId,
          actionedByUserName: authUserName,
        });
      }),
    );
  }
}

export default Form;
export { FormState, FormType };
